"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { LOCALES, LOCALE_NAMES, pathForLocale, type Locale } from "@/i18n/config";
import { useI18n } from "@/i18n/dictionary-context";

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <circle cx="8" cy="8" r="6" />
      <path d="M2 8h12M8 2a8.5 8.5 0 0 1 0 12M8 2a8.5 8.5 0 0 0 0 12" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d="m3.5 8.5 3 3 6-7" />
    </svg>
  );
}

export function LanguageToggle({ full = false }: { full?: boolean }) {
  const { locale } = useI18n();
  const router = useRouter();
  const pathname = usePathname() ?? "/";
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function selectLocale(target: Locale) {
    if (typeof document !== "undefined") {
      document.cookie = `NEXT_LOCALE=${target}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    }
    const newPath = pathForLocale(pathname, target);
    setOpen(false);
    router.push(newPath);
    router.refresh();
  }

  const trigger = full ? (
    <button
      type="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      onClick={() => setOpen((o) => !o)}
      className="flex h-11 w-full items-center justify-between rounded-lg border border-neutral-200 bg-white px-4 text-base font-medium tracking-tight text-neutral-950 hover:bg-neutral-50"
    >
      <span className="inline-flex items-center gap-2">
        <GlobeIcon className="size-4" />
        {LOCALE_NAMES[locale]}
      </span>
      <span className="text-xs text-neutral-400">{locale.toUpperCase()}</span>
    </button>
  ) : (
    <button
      type="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      onClick={() => setOpen((o) => !o)}
      className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-950"
    >
      <GlobeIcon className="size-4" />
      <span className="font-semibold">{locale.toUpperCase()}</span>
    </button>
  );

  return (
    <div ref={ref} className="relative">
      {trigger}
      {open && (
        <ul
          role="listbox"
          className={`absolute z-50 ${full ? "left-0 right-0 mt-2" : "right-0 mt-2 min-w-[180px]"} overflow-hidden rounded-xl border border-neutral-200 bg-white py-1 shadow-[0_24px_48px_-24px_rgba(14,18,27,0.22)]`}
        >
          {LOCALES.map((l) => {
            const active = l === locale;
            return (
              <li key={l}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => selectLocale(l)}
                  className={`flex w-full items-center justify-between gap-3 px-3.5 py-2 text-left text-sm transition-colors ${
                    active
                      ? "bg-brand-soft text-brand"
                      : "text-neutral-700 hover:bg-neutral-50 hover:text-neutral-950"
                  }`}
                >
                  <span className="font-medium tracking-tight">
                    {LOCALE_NAMES[l]}
                  </span>
                  {active && <CheckIcon className="size-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
